/**
 * Cookie consent banner - CHAP-135 (BLOCKS-CUTOVER).
 *
 * Three categories per `gdpr-ccpa-scope-decision.md`:
 *   - **Strictly necessary** - auth + CSRF + session; always on, no opt-out
 *   - **Analytics** - PostHog product analytics (CHAP-42 spike); default
 *     OFF in EU/CA per IP heuristic, default OFF everywhere v1 (opt-in)
 *   - **Marketing** - none in v1; placeholder for future
 *
 * Consent is stored in localStorage so it persists per-browser. Resetting
 * consent is always available via the /legal/cookies page (visible link
 * in the footer + privacy page).
 *
 * Banner appears at the BOTTOM of the viewport on first visit. Two
 * actions:
 *   - "Accept all" - sets all three categories to true
 *   - "Reject non-essential" - sets necessary=true, others=false
 *   - "Customize" - opens the /legal/cookies preferences page
 *
 * Per the GDPR-flavored posture, "Reject" must be as easy as "Accept";
 * dark-pattern Accept-only banners are non-compliant.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const CONSENT_KEY = 'chapterbuild_cookie_consent';
const CONSENT_VERSION = 1; // Bump when policy materially changes; banner reappears

export interface CookieConsent {
  necessary: true;
  analytics: boolean;
  marketing: boolean;
  decided_at: string;
  version: number;
}

export function readConsent(): CookieConsent | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<CookieConsent>;
    // Re-prompt if the policy version changed
    if (parsed.version !== CONSENT_VERSION) return null;
    return {
      necessary: true,
      analytics: Boolean(parsed.analytics),
      marketing: Boolean(parsed.marketing),
      decided_at: String(parsed.decided_at ?? ''),
      version: CONSENT_VERSION,
    };
  } catch {
    return null;
  }
}

export function writeConsent(c: Omit<CookieConsent, 'version' | 'decided_at'>): void {
  if (typeof window === 'undefined') return;
  const full: CookieConsent = {
    ...c,
    necessary: true, // can't turn off
    decided_at: new Date().toISOString(),
    version: CONSENT_VERSION,
  };
  localStorage.setItem(CONSENT_KEY, JSON.stringify(full));
}

export default function CookieBanner() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const existing = readConsent();
    if (!existing) setShow(true);
  }, []);

  if (!show) return null;

  const acceptAll = () => {
    writeConsent({ necessary: true, analytics: true, marketing: true });
    setShow(false);
  };
  const rejectNonEssential = () => {
    writeConsent({ necessary: true, analytics: false, marketing: false });
    setShow(false);
  };

  return (
    <div
      role="dialog"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-desc"
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'white',
        borderTop: '2px solid #03244d',
        boxShadow: '0 -4px 16px rgba(3,36,77,0.12)',
        padding: '16px 24px',
        zIndex: 9999,
        fontFamily: 'system-ui, sans-serif',
      }}
    >
      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'flex-start',
          gap: 24,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: 1, minWidth: 280 }}>
          <h3
            id="cookie-banner-title"
            style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 600, color: '#03244d' }}
          >
            Cookies + privacy
          </h3>
          <p id="cookie-banner-desc" style={{ margin: 0, fontSize: 14, color: '#374151' }}>
            We use strictly-necessary cookies to keep you signed in. Analytics
            cookies (off by default) help us improve the dashboard. No
            advertising cookies, ever. See our{' '}
            <Link to="/legal/privacy" style={{ color: '#03244d', textDecoration: 'underline' }}>
              privacy policy
            </Link>
            {' '}or{' '}
            <Link to="/legal/cookies" style={{ color: '#03244d', textDecoration: 'underline' }}>
              cookie preferences
            </Link>
            {' '}for details.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button
            type="button"
            onClick={rejectNonEssential}
            style={{
              padding: '8px 16px',
              background: 'white',
              border: '1.5px solid #03244d',
              color: '#03244d',
              borderRadius: 6,
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Reject non-essential
          </button>
          <button
            type="button"
            onClick={acceptAll}
            style={{
              padding: '8px 16px',
              background: '#03244d',
              border: '1.5px solid #03244d',
              color: 'white',
              borderRadius: 6,
              fontSize: 14,
              fontWeight: 500,
              cursor: 'pointer',
            }}
          >
            Accept all
          </button>
        </div>
      </div>
    </div>
  );
}
